import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  AlarmClock, ArrowRight, MapPin, FileText, Send, CheckCircle2, ScrollText,
  Euro, CalendarDays, Clock, XCircle, Sparkles, Bell, AlertTriangle,
  Heart, ScrollText as ContractIcon, Wallet, Mail, Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLeads } from "@/context/LeadsContext";
import { InfoTip } from "@/components/InfoTip";
import FinancialValue from "@/components/FinancialValue";
import FinancePinShield from "@/components/FinancePinShield";
import { TIPS } from "@/lib/infoTips";
import LeadMachineCard from "@/components/LeadMachineCard";
import StateBadge from "@/components/agenda/StateBadge";

const MONTHS = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const WEEKDAYS = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];

const fmtEuro = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(parseFloat(n) || 0);

function fmtDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return `${d} ${MONTHS[m - 1]}`;
}
function weekdayOf(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return WEEKDAYS[(dt.getDay() + 6) % 7];
}

function KpiCard({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div className="bg-white border border-border p-5 rounded-sm" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="label-uppercase">{label}</div>
        <Icon className={`w-5 h-5 ${accent || "text-primary"}`} strokeWidth={1.5} />
      </div>
      <div className="font-serif-heading text-4xl mt-2">{value}</div>
      {sub && <div className="text-xs text-neutral-500 mt-1">{sub}</div>}
    </div>
  );
}

/**
 * Card "Oggi devi fare" — gli item più urgenti del giorno (max 5).
 * Backed by today_todo dal backend (matrimonio del giorno + acconti scaduti +
 * solleciti contratti + solleciti preventivi).
 */
function TodayTodoCard({ data, onNavigate }) {
  const items = data?.items || [];
  const total = data?.total || 0;
  const urgent = data?.urgent || 0;

  if (total === 0) {
    return (
      <section className="bg-gradient-to-br from-[#FFF8EC] to-white border border-[#EAE3D8] rounded-sm p-6" data-testid="today-todo-empty">
        <div className="flex items-center gap-3 mb-2">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
          <span className="label-uppercase text-[#7B6A45]">Oggi devi fare</span>
        </div>
        <div className="font-serif-heading text-2xl text-neutral-700">Tutto in ordine</div>
        <div className="text-sm text-neutral-500 mt-1">
          Nessuna attività urgente per oggi. Goditi il tempo libero o lavora ai preventivi in bozza.
        </div>
      </section>
    );
  }

  const ICON_FOR_KIND = {
    wedding: Heart,
    contract: ContractIcon,
    quote: Mail,
    deposit: Wallet,
  };

  return (
    <section
      className={`rounded-sm p-6 border ${
        urgent > 0
          ? "bg-gradient-to-br from-[#FFF6E6] to-white border-amber-500/60 shadow-[0_2px_8px_rgba(245,158,11,0.12)]"
          : "bg-white border-[#EAE3D8]"
      }`}
      data-testid="today-todo-card"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <AlertTriangle className={`w-5 h-5 ${urgent > 0 ? "text-amber-600 animate-pulse" : "text-[#7B6A45]"}`} strokeWidth={1.5} />
          <div>
            <span className="label-uppercase text-[#7B6A45]">Oggi devi fare</span>
            <div className="font-serif-heading text-2xl">{total} {total === 1 ? "attività" : "attività"}</div>
          </div>
        </div>
        {urgent > 0 && (
          <span className="px-3 py-1 rounded-full bg-amber-500 text-white text-[11px] font-bold uppercase tracking-wider" data-testid="today-urgent-badge">
            {urgent} urgenti
          </span>
        )}
      </div>
      <div className="space-y-2">
        {items.map((it, i) => {
          const Icon = ICON_FOR_KIND[it.kind] || Sparkles;
          return (
            <button
              key={i}
              onClick={() => onNavigate(`/app/clients/${it.client_id}`)}
              className={`w-full text-left p-3 rounded-sm border transition-all group flex items-center gap-3 ${
                it.urgent
                  ? "bg-white border-amber-300 hover:border-amber-500"
                  : "bg-white/70 border-neutral-200 hover:border-[#C8A97E]"
              }`}
              data-testid={`today-todo-item-${i}`}
            >
              <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                it.urgent ? "bg-amber-100 text-amber-700" : "bg-neutral-100 text-neutral-600"
              }`}>
                <Icon className="w-4 h-4" strokeWidth={1.5} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-neutral-900 truncate">{it.label}</div>
                {it.detail && <div className="text-xs text-neutral-500 truncate mt-0.5">{it.detail}</div>}
              </div>
              <ArrowRight className="w-4 h-4 text-neutral-400 group-hover:text-[#7B6A45] group-hover:translate-x-1 transition-all" strokeWidth={1.5} />
            </button>
          );
        })}
      </div>
    </section>
  );
}

/**
 * Card "Prossimi 7 giorni" — timeline compatta read-only.
 * Mostra matrimoni e prove con il loro stato derivato.
 */
function Next7DaysCard({ items, onNavigate }) {
  if (!items || items.length === 0) {
    return (
      <section className="bg-white border border-[#EAE3D8] rounded-sm p-6" data-testid="next-7-empty">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-[#7B6A45]" strokeWidth={1.5} />
            <span className="label-uppercase text-[#7B6A45]">Prossimi 7 giorni</span>
          </div>
          <Link to="/app/agenda" className="text-xs text-[#7B6A45] hover:underline" data-testid="next-7-link-agenda">
            Apri Agenda →
          </Link>
        </div>
        <div className="text-sm text-neutral-500 mt-3">Nessun evento nei prossimi 7 giorni.</div>
      </section>
    );
  }
  // Group by date
  const groups = items.reduce((acc, it) => {
    const k = it.date;
    if (!acc[k]) acc[k] = [];
    acc[k].push(it);
    return acc;
  }, {});
  const keys = Object.keys(groups).sort();

  return (
    <section className="bg-white border border-[#EAE3D8] rounded-sm p-6" data-testid="next-7-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-[#7B6A45]" strokeWidth={1.5} />
          <span className="label-uppercase text-[#7B6A45]">Prossimi 7 giorni</span>
        </div>
        <Link to="/app/agenda" className="text-xs text-[#7B6A45] hover:underline" data-testid="next-7-link-agenda">
          Apri Agenda →
        </Link>
      </div>
      <div className="space-y-3">
        {keys.map((dateIso) => (
          <div key={dateIso} className="flex gap-4">
            <div className="w-16 shrink-0 text-right pt-1">
              <div className="font-serif-heading text-lg leading-none text-[#7B6A45]">{fmtDate(dateIso)}</div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-400 mt-1">{weekdayOf(dateIso)}</div>
            </div>
            <div className="flex-1 space-y-1.5 border-l border-[#EAE3D8] pl-4">
              {groups[dateIso].map((ev, i) => (
                <button
                  key={i}
                  onClick={() => onNavigate(`/app/clients/${ev.client_id}`)}
                  className="w-full text-left flex items-center gap-2 py-1.5 px-2 rounded hover:bg-[#FFF8EC] group"
                  data-testid={`next-7-item-${ev.client_id}-${ev.kind}`}
                >
                  <StateBadge state={ev.state} className="!text-[9px]" />
                  <span className="text-sm text-neutral-800 flex-1 truncate">
                    {ev.client_name}
                    {ev.kind === "trial" && <span className="text-neutral-500"> · Prova {ev.trial_label?.toLowerCase()}</span>}
                  </span>
                  {ev.ceremony_time && <span className="text-xs text-neutral-400 shrink-0">{ev.ceremony_time}</span>}
                  {ev.time && <span className="text-xs text-neutral-400 shrink-0">{ev.time}</span>}
                  <ArrowRight className="w-3.5 h-3.5 text-neutral-300 group-hover:text-[#7B6A45] group-hover:translate-x-0.5 transition-all" strokeWidth={1.5} />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Widget amministrativo riusabile. Mostra fino a 5 row con CTA per andare alla
 * scheda cliente. Header con count totale.
 */
function AdminWidget({ title, icon: Icon, accentColor, items, total, emptyText, renderRow, onNavigate, testid }) {
  return (
    <section className="bg-white border border-[#EAE3D8] rounded-sm p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${accentColor}`} strokeWidth={1.5} />
          <span className="label-uppercase text-[#7B6A45]">{title}</span>
        </div>
        <span className="font-serif-heading text-2xl text-neutral-700" data-testid={`${testid}-count`}>
          {total || 0}
        </span>
      </div>
      {(!items || items.length === 0) ? (
        <div className="text-xs text-neutral-400 italic py-4 text-center">{emptyText}</div>
      ) : (
        <div className="space-y-1.5">
          {items.slice(0, 5).map((it, i) => (
            <button
              key={i}
              onClick={() => onNavigate(`/app/clients/${it.client_id}`)}
              className="w-full text-left flex items-center gap-2 p-2 rounded hover:bg-[#FFF8EC] group border border-transparent hover:border-[#EAE3D8] transition-all"
              data-testid={`${testid}-item-${i}`}
            >
              {renderRow(it)}
              <ArrowRight className="w-3.5 h-3.5 text-neutral-300 group-hover:text-[#7B6A45] group-hover:translate-x-0.5 transition-all shrink-0" strokeWidth={1.5} />
            </button>
          ))}
          {items.length > 5 && (
            <div className="text-[11px] text-neutral-400 text-center pt-1">+ {items.length - 5} altri</div>
          )}
        </div>
      )}
    </section>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [operational, setOperational] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/dashboard/reminders").then((r) => setReminders(r.data?.reminders || [])).catch(() => {});
    api.get("/dashboard/operational").then((r) => setOperational(r.data)).catch(() => {});
    // Surface unread notifications (consume-on-read on the backend)
    api.get("/notifications").then((r) => {
      const list = r.data?.notifications || [];
      list.forEach((n) => {
        if (n.kind === "quote_accepted") {
          toast.success(
            `${n.client_name} ha accettato il preventivo`,
            { description: "Il contratto è stato firmato digitalmente. Cliente aggiornato a Confermata.", icon: <CheckCircle2 className="w-4 h-4 text-emerald-600" strokeWidth={1.5} /> }
          );
        } else if (n.kind === "quote_rejected") {
          toast.error(
            `${n.client_name} ha rifiutato il preventivo`,
            { description: n.extra?.reason ? `Motivo: ${n.extra.reason}` : "Nessun motivo specificato.", icon: <XCircle className="w-4 h-4 text-red-600" strokeWidth={1.5} /> }
          );
        } else if (n.kind === "quote_options_updated") {
          const changes = (n.extra?.changes || []).filter((c) => c.selected);
          const added = changes.length;
          const newTotal = n.extra?.new_total;
          const desc = added > 0
            ? `Servizi aggiunti: ${changes.map((c) => c.desc).join(", ")}${typeof newTotal === "number" ? ` · Nuovo totale: € ${newTotal.toFixed(2)}` : ""}`
            : "Selezioni opzionali aggiornate.";
          toast.info(
            `${n.client_name} ha aggiornato i servizi opzionali`,
            { description: desc, icon: <Sparkles className="w-4 h-4 text-primary" strokeWidth={1.5} /> }
          );
        }
      });
    }).catch(() => {});
  }, []);

  const todayTodo = operational?.today_todo || { items: [], total: 0, urgent: 0 };
  const next7 = operational?.next_7_days || [];
  const pendingQuotes = operational?.pending_quotes || [];
  const pendingContracts = operational?.pending_contracts || [];
  const pendingDeposits = operational?.pending_deposits || [];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <span className="label-uppercase">Benvenuta, {user?.name?.split(" ")[0] || ""}</span>
        <h1 className="font-serif-heading text-4xl sm:text-5xl tracking-tight mt-2">Centro Operativo</h1>
        <p className="text-sm text-neutral-500 mt-2 max-w-2xl">
          La tua dashboard di controllo: cosa fare oggi, cosa arriva nei prossimi giorni e dove servono solleciti.
          La visione temporale completa è nell'<Link to="/app/agenda" className="text-[#7B6A45] hover:underline" data-testid="link-to-agenda">Agenda</Link>.
        </p>
      </div>

      {/* Lead Machine — primary acquisition tool */}
      <LeadMachineCard />

      <LeadsBanner />

      {/* HERO: Oggi devi fare + Prossimi 7 giorni (2 colonne desktop) */}
      <div className="grid lg:grid-cols-2 gap-5">
        <TodayTodoCard data={todayTodo} onNavigate={navigate} />
        <Next7DaysCard items={next7} onNavigate={navigate} />
      </div>

      {/* Reminders 24h (legacy, mantenuto perché ha dettagli ceremony/work_start non in operational) */}
      {reminders.length > 0 && (
        <div className="bg-primary/5 border border-primary/30 rounded-sm p-6" data-testid="dashboard-reminders">
          <div className="flex items-center gap-3 mb-4">
            <AlarmClock className="w-5 h-5 text-primary animate-pulse" strokeWidth={1.5} />
            <span className="label-uppercase text-primary">Promemoria · Prossime 24 ore</span>
          </div>
          <div className="space-y-3">
            {reminders.map((r) => (
              <Link
                key={r.client_id}
                to={`/app/clients/${r.client_id}`}
                className="flex items-center justify-between p-4 bg-white border border-border rounded-sm hover:border-primary transition-all group"
                data-testid={`reminder-${r.client_id}`}
              >
                <div className="flex-1">
                  <div className="font-serif-heading text-xl">{r.client_name}</div>
                  <div className="text-xs text-neutral-500 mt-1 flex flex-wrap gap-3">
                    <span>{r.wedding_date}</span>
                    {r.work_start_time && <span>Inizio lavoro {r.work_start_time}</span>}
                    {r.ceremony_time && <span>Cerimonia {r.ceremony_time}</span>}
                    {r.people_count > 1 && <span>{r.people_count} persone</span>}
                    {r.prep_venue && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" strokeWidth={1.5} />{r.prep_venue}</span>}
                  </div>
                </div>
                <div className="text-right ml-4">
                  <div className="font-serif-heading text-2xl text-primary">
                    {r.hours_until <= 0 ? "Ora" : `${r.hours_until}h`}
                  </div>
                  <ArrowRight className="w-4 h-4 inline text-neutral-400 group-hover:text-primary group-hover:translate-x-1 transition-all" strokeWidth={1.5} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Widget admin: Preventivi · Contratti · Acconti */}
      <section>
        <div className="label-uppercase mb-3 flex items-center gap-1.5">
          Azioni amministrative
          <InfoTip {...TIPS.dash_kpi} data-testid="tip-dash-admin" />
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <AdminWidget
            title="Preventivi da seguire"
            icon={Mail}
            accentColor="text-amber-600"
            items={pendingQuotes}
            total={pendingQuotes.length}
            emptyText="Nessun preventivo in attesa"
            onNavigate={navigate}
            testid="widget-pending-quotes"
            renderRow={(it) => (
              <>
                <StateBadge state="inviato" className="!text-[8px]" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-neutral-900 truncate">{it.client_name}</div>
                  <div className="text-[10.5px] text-neutral-500 flex items-center gap-2">
                    {it.quote_read_at ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700"><Eye className="w-3 h-3" />letto</span>
                    ) : (
                      <span className="text-neutral-400">non letto</span>
                    )}
                    <span>·</span>
                    <span>attesa {it.attesa_gg}gg</span>
                  </div>
                </div>
                <span className="text-[11px] text-neutral-600 shrink-0">{fmtEuro(it.quote_amount)}</span>
              </>
            )}
          />
          <AdminWidget
            title="Contratti da firmare"
            icon={ContractIcon}
            accentColor="text-blue-600"
            items={pendingContracts}
            total={pendingContracts.length}
            emptyText="Nessun contratto in attesa"
            onNavigate={navigate}
            testid="widget-pending-contracts"
            renderRow={(it) => (
              <>
                <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-neutral-900 truncate">{it.client_name}</div>
                  <div className="text-[10.5px] text-neutral-500">
                    {it.sent_days_ago > 0 ? `inviato ${it.sent_days_ago}gg fa` : "appena inviato"}
                  </div>
                </div>
                {it.sent_days_ago >= 5 && (
                  <span className="text-[9px] uppercase font-bold tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded shrink-0">
                    Solleciti
                  </span>
                )}
              </>
            )}
          />
          <AdminWidget
            title="Acconti mancanti"
            icon={Wallet}
            accentColor="text-rose-600"
            items={pendingDeposits}
            total={pendingDeposits.length}
            emptyText="Tutti gli acconti ricevuti"
            onNavigate={navigate}
            testid="widget-pending-deposits"
            renderRow={(it) => (
              <>
                <span className={`w-2 h-2 rounded-full shrink-0 ${it.is_overdue ? "bg-rose-500 animate-pulse" : "bg-amber-400"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-neutral-900 truncate">{it.client_name}</div>
                  <div className="text-[10.5px] text-neutral-500">
                    {it.is_overdue ? (
                      <span className="text-rose-700 font-semibold">in ritardo</span>
                    ) : (
                      <span>in {it.days_to_wedding}gg matrimonio</span>
                    )}
                  </div>
                </div>
                <span className="text-[11px] font-semibold text-neutral-700 shrink-0">{fmtEuro(it.amount)}</span>
              </>
            )}
          />
        </div>
      </section>

      {/* KPI Preventivi & Contratti (visione storica) */}
      <section>
        <div className="label-uppercase mb-3 flex items-center gap-1.5">
          Pipeline · Preventivi & Contratti
          <InfoTip {...TIPS.dash_kpi} data-testid="tip-dash-kpi" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          <KpiCard testid="kpi-quote-drafts" icon={FileText} label="Bozza" value={stats?.quote_drafts ?? "—"} sub="In preparazione" accent="text-blue-500" />
          <KpiCard testid="kpi-quote-sent" icon={Send} label="Inviati" value={stats?.quote_sent ?? "—"} sub="In attesa risposta" accent="text-amber-500" />
          <KpiCard testid="kpi-quote-accepted" icon={CheckCircle2} label="Accettati" value={stats?.quote_accepted ?? "—"} sub="Da firmare" accent="text-emerald-500" />
          <KpiCard testid="kpi-contract-signed" icon={ScrollText} label="Contratti firmati" value={stats?.contract_signed ?? "—"} sub="Confermati" accent="text-emerald-600" />
        </div>
      </section>

      {/* Fatturato */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="label-uppercase">Fatturato</div>
          <FinancePinShield />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="bg-white border border-border rounded-sm p-6 flex items-center justify-between" data-testid="revenue-potential">
            <div>
              <div className="label-uppercase text-amber-600">Potenziale</div>
              <div className="font-serif-heading text-4xl mt-2">
                <FinancialValue value={fmtEuro(stats?.revenue_potential)} testid="revenue-potential-value" />
              </div>
              <div className="text-xs text-neutral-500 mt-1">Da preventivi inviati</div>
            </div>
            <Euro className="w-8 h-8 text-amber-400" strokeWidth={1.2} />
          </div>
          <div className="bg-primary/5 border border-primary/30 rounded-sm p-6 flex items-center justify-between" data-testid="revenue-confirmed">
            <div>
              <div className="label-uppercase text-emerald-700">Confermato</div>
              <div className="font-serif-heading text-4xl mt-2 text-primary">
                <FinancialValue value={fmtEuro(stats?.revenue_confirmed)} testid="revenue-confirmed-value" />
              </div>
              <div className="text-xs text-neutral-500 mt-1">Da contratti firmati / preventivi accettati</div>
            </div>
            <Euro className="w-8 h-8 text-primary" strokeWidth={1.2} />
          </div>
        </div>
      </section>

      {/* Quick actions */}
      <section className="bg-white border border-border rounded-sm p-6">
        <div className="label-uppercase mb-4">Azioni rapide</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <Link to="/app/clients"><Button variant="outline" className="w-full justify-start rounded-sm" data-testid="quick-new-client">+ Nuovo cliente</Button></Link>
          <Link to="/app/agenda"><Button variant="outline" className="w-full justify-start rounded-sm" data-testid="quick-new-event">Apri agenda</Button></Link>
          <Link to="/app/team"><Button variant="outline" className="w-full justify-start rounded-sm" data-testid="quick-new-team">+ Aggiungi al team</Button></Link>
        </div>
      </section>
    </div>
  );
}

// Persistent banner shown when there are unread leads (status="lead", seen=false).
// Each request links DIRECTLY to its own client card (/app/clients/{id}) —
// opening the detail is what marks it as read (GET /clients/{cid} → seen=true).
function LeadsBanner() {
  const { count, items } = useLeads();
  if (!count) return null;
  const shown = items.slice(0, 3);
  const rest = Math.max(0, count - shown.length);
  return (
    <div className="relative bg-red-50 border border-red-200 rounded-sm p-5" data-testid="dashboard-leads-banner-wrapper">
      <span className="absolute right-4 top-4 z-10">
        <InfoTip {...TIPS.dash_leads_banner} side="bottom" align="end" data-testid="tip-dash-leads" />
      </span>
      <div className="flex items-start gap-4" data-testid="dashboard-leads-banner">
        <span className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0 relative">
          <Bell className="w-5 h-5 text-red-700" strokeWidth={1.5} />
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold tabular-nums">
            {count > 99 ? "99+" : count}
          </span>
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-red-900 pr-8">
            {count === 1 ? "Nuova richiesta preventivo ricevuta" : `${count} nuove richieste preventivo ricevute`}
          </div>
          <ul className="mt-2 divide-y divide-red-100 border-y border-red-100" data-testid="dashboard-leads-list">
            {shown.map((it) => (
              <li key={it.id}>
                <Link
                  to={`/app/clients/${it.id}`}
                  className="flex items-center gap-3 py-2 hover:bg-red-100/60 -mx-2 px-2 rounded transition-colors"
                  data-testid={`dashboard-lead-item-${it.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-red-950 truncate">{it.name || it.email || "—"}</div>
                    <div className="text-xs text-red-700 truncate">
                      {[it.wedding_date ? fmtDate(it.wedding_date) : null, it.venue || null].filter(Boolean).join(" · ") || it.email || ""}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-red-700 shrink-0" strokeWidth={1.5} />
                </Link>
              </li>
            ))}
          </ul>
          {(count > 1 || rest > 0) && (
            <Link to="/app/clients?status=new" className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-red-800 hover:underline" data-testid="dashboard-leads-see-all">
              Vedi tutte le nuove richieste{rest > 0 ? ` (+${rest})` : ""} <ArrowRight className="w-3 h-3" strokeWidth={1.8} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
